import { useEffect, useMemo, useState } from 'react';
import { StanButton, StanDropdown } from '@/components';
import { createIconElement } from '@/lib';
import { countries } from '@/components/core-components/StanPhoneInput/countries';
import { STRIPE_SUPPORTED_COUNTRIES } from '@/lib/constants/stripeCountries';
import StripeLogo from '@/assets/icons/payments/stripe.svg';

interface StripeAccountConnectProps {
    onConnect: (country: string) => void;
    standard?: boolean;
}

export const StripeAccountConnect = ({ onConnect, standard = false }: StripeAccountConnectProps) => {
    const [country, setCountry] = useState('US');
    const [processing, setProcessing] = useState(false);

    // Filter existing country data to only Stripe-supported countries
    const availableCountries = useMemo(() => {
        return countries.filter(c => STRIPE_SUPPORTED_COUNTRIES.includes(c.code));
    }, []);

    // For standard, auto-connect
    useEffect(() => {
        if (standard) {
            handleConnect();
        }
    }, [standard]);

    const handleConnect = () => {
        setProcessing(true);
        onConnect(country);
    };

    return (
        <div className='bg-bg-canvas flex min-h-screen flex-col'>
            <div className='container mx-auto mt-12 flex flex-col px-4'>
                <div className='mb-8 flex items-center justify-center gap-4 text-center'>
                    <div className='bg-brand-main flex size-16 items-center justify-center rounded-full'>
                        <span className='text-heading-h1 text-canvas'>$</span>
                    </div>
                    <span className='text-heading-h1'>🤝</span>
                    {createIconElement(StripeLogo, { className: 'h-16 w-auto', removeFill: false })}
                </div>

                <div className='mt-8 text-center'>
                    <h2 className='text-heading-h2 font-bold'>Start Selling & Get Paid</h2>
                    <p className='text-body-1-regular text-gray mt-4'>
                        Stan partners with Stripe for payment processing.
                    </p>
                    <p className='text-body-1-regular text-gray mt-3'>
                        Enjoy 0% transaction fees from Stan only paying Stripe&apos;s processing fee.
                    </p>
                </div>
            </div>

            <div className='container mx-auto mt-12 max-w-md px-4 text-center'>
                {!standard && (
                    <div className='mb-6 text-left'>
                        <label className='text-body-1-regular mb-2 block font-bold'>Home Country</label>
                        <StanDropdown
                            id='country-select'
                            name='country'
                            options={availableCountries.map(c => ({
                                label: c.name,
                                value: c.code,
                                icon: c.image,
                            }))}
                            currentValue={{
                                label: availableCountries.find(c => c.code === country)?.name || 'United States',
                                value: country,
                            }}
                            onSelect={value => setCountry(value.value)}
                            placeholder='Select country'
                        />
                    </div>
                )}

                <StanButton
                    name='stripe-connect-button'
                    className='w-full'
                    loading={processing}
                    disabled={!country || processing}
                    onClick={handleConnect}
                >
                    Create My Stan Stripe Account
                </StanButton>

                <p className='text-caption-1-regular text-gray mt-4'>You will be redirected to connect.stripe.com.</p>
            </div>
        </div>
    );
};
